import { cloneElement, useMemo, useState } from "react";
import TurkeyMap from "turkey-map-react";
import { Card } from "@/components/ui/card";
import { MapPin } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");
const norm = (s) => (s ?? "").toString().trim().toLocaleLowerCase("tr");

// Yoğunluğa göre mavi tonlu renk skalası (koyu = yoğun)
const RAMP = ["#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8"];
const IDLE = "#94a3b8";

function colorFor(count, max) {
  if (!count || !max) return IDLE;
  const t = count / max;
  if (t > 0.8) return RAMP[4];
  if (t > 0.6) return RAMP[3];
  if (t > 0.4) return RAMP[2];
  if (t > 0.2) return RAMP[1];
  return RAMP[0];
}

export default function DensityMap({ records }) {
  const [selected, setSelected] = useState(null);

  const stats = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const il = (r.values?.il ?? "").toString().trim();
      if (!il) return;
      const key = norm(il);
      if (!map[key]) map[key] = { il, count: 0, ciro: 0, hedef: 0 };
      map[key].count += 1;
      map[key].ciro += parseFloat(r.values?.gerceklesen_ciro) || 0;
      map[key].hedef += parseFloat(r.values?.hedef) || 0;
    });
    return map;
  }, [records]);

  const max = useMemo(
    () => Object.values(stats).reduce((m, v) => Math.max(m, v.count), 0),
    [stats]
  );

  const ranked = useMemo(
    () => Object.values(stats).sort((a, b) => b.count - a.count),
    [stats]
  );

  const renderCity = (cityComponent, city) => {
    const s = stats[norm(city.name)];
    const count = s?.count || 0;
    const isSel = selected && norm(selected.il) === norm(city.name);
    const path = cityComponent.props.children;
    const newPath = cloneElement(path, {
      style: {
        ...(path.props.style || {}),
        fill: colorFor(count, max),
        stroke: isSel ? "#f59e0b" : "#0f172a",
        strokeWidth: isSel ? 1.4 : 0.4,
        transition: "fill .2s, stroke .2s",
      },
    });
    return cloneElement(cityComponent, { key: city.id }, newPath);
  };

  const selectCity = (city) => {
    const s = stats[norm(city.name)];
    setSelected(s || { il: city.name, count: 0, ciro: 0, hedef: 0 });
  };

  return (
    <Card className="p-4" data-testid="density-map">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
          <MapPin className="w-4 h-4" /> Müşteri Yoğunluk Haritası
        </h3>
        <span className="text-xs text-muted-foreground">İl bazlı müşteri dağılımı</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <div className="min-w-0">
          <div className="w-full [&_svg]:w-full [&_svg]:h-auto" data-testid="turkey-map">
            <TurkeyMap
              hoverable={false}
              showTooltip={false}
              cityWrapper={renderCity}
              onClick={selectCity}
              customStyle={{ idleColor: IDLE, hoverColor: "#f59e0b" }}
            />
          </div>
          {/* Renk skalası */}
          <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground" data-testid="map-legend">
            <span>Az</span>
            {RAMP.map((c) => (
              <span key={c} className="h-3 w-6 rounded-sm" style={{ background: c }} />
            ))}
            <span>Çok</span>
            {max > 0 && <span className="ml-2">· En yüksek: {max} müşteri</span>}
          </div>
        </div>

        <div className="space-y-3">
          {selected ? (
            <div className="rounded-lg border border-border p-3 bg-secondary/40" data-testid="map-selected">
              <div className="font-semibold text-sm mb-2">{selected.il}</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Müşteri</span>
                  <span className="font-medium">{selected.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hedef</span>
                  <span className="font-medium text-primary">₺{fmt(selected.hedef)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ciro</span>
                  <span className="font-medium text-emerald-500">₺{fmt(selected.ciro)}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3">
              Detay için haritadan bir il seçin.
            </p>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
              En yoğun iller
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto grid-scroll">
              {ranked.slice(0, 15).map((row) => (
                <button
                  key={row.il}
                  onClick={() => setSelected(row)}
                  className="w-full flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-secondary transition-colors"
                  data-testid={`map-rank-${row.il}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span
                      className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                      style={{ background: colorFor(row.count, max) }}
                    />
                    <span className="truncate">{row.il}</span>
                  </span>
                  <span className="font-semibold">{row.count}</span>
                </button>
              ))}
              {ranked.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">Veri yok.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
